"use client";

import React, { PropsWithChildren, useRef, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
// ! WARNING: Barrel file export @app/api will cause compile errors due to importing server components (barrel file exports request.ts which calls server-side session), so import these two files directly
import { ContactsQuery } from "src/lib/api/EntityQuery";
import { FilterQueryParams } from "src/lib/api/FilterQueryParams";

import { NotificationProvider } from "@fsg/gui-bits";

import { ENDPOINTS } from "@app/constants";
import {
  APIResponse,
  ContactResponse,
  CountryResponseType,
  CreationSuccess,
  Division,
  StateResponse,
  FSGServices,
  MarketingSource,
} from "@app/types";
import { FormRegistry } from "@app/utils/formRegistry";

import { NewLeadFormData } from "../Create/Lead";
import { Session, Status, useRequestOnMount } from "src/fsg-next-auth/src";
import { useAppSetup } from "@app/hooks/useAppSetup";
import { ImageData } from "@app/utils";
import { divisionsFallback } from "src/fsg-utils/src/utils/divisionsFallback";

type RequestState<T> = { data: T[]; status: Status; error: Error | null };

type AppContextState = {
  countries: CountryResponseType;
  divisions: RequestState<Division>;
  session: Session;
  userContact: ContactResponse;
  userContactSearch: ReturnType<
    typeof useRequestOnMount<APIResponse<ContactResponse>>
  >;
  showCreateLead: boolean;
  setShowCreateLead: React.Dispatch<React.SetStateAction<boolean>>;
  showCreateSite: boolean;
  setShowCreateSite: React.Dispatch<React.SetStateAction<boolean>>;
  leadData: NewLeadFormData;
  setLeadData: React.Dispatch<React.SetStateAction<NewLeadFormData>>;
  businessCardPhoto: string | null;
  setBusinessCardPhoto: React.Dispatch<React.SetStateAction<string | null>>;
  isDataReady: boolean;
  isDiv99: boolean;
  isCoolKids: boolean;
  formRegistry: FormRegistry;
  onCreateFunctions: CreationSuccess;
  setOnCreateFunctions: React.Dispatch<React.SetStateAction<CreationSuccess>>;
  states: StateResponse[];
  provinces: StateResponse[];
  services: FSGServices[];
  sources: MarketingSource[];
  pendingBusinessCardImage: any | null;
  setPendingBusinessCardImage: React.Dispatch<React.SetStateAction<any | null>>;
};
interface ProviderProps extends PropsWithChildren {
  session: Session;
}

const AppContext = React.createContext<AppContextState>(undefined);
const { Provider } = AppContext;

if (process.env.ENVIRONMENT !== "IS_DEV") {
  console.debug(
    "====================\n>> datadogRum.init()\n===================="
  );
  datadogRum.init({
    applicationId: "6e7049de-efdf-40c2-b68b-7f44e609451d",
    clientToken: "pubb084dac4c3d1d4bed5b1119ce6998e2c",
    site: "us5.datadoghq.com",
    service: "engage",
    env: "dev",
    version: "0.1.0", // * Specify a version number to identify the deployed version of your application in Datadog
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
  });
  datadogRum.startSessionReplayRecording();
}

function buildUserContactQuery(session: Session) {
  const contactQuery = new ContactsQuery();
  const q = new FilterQueryParams();
  const filters = [];

  // sometimes fsgEmployeeId is null, fallback to email if it is
  const employeeNumber = session?.user?.employeeNumber;
  if (employeeNumber) {
    const exactMatch = q.textFilter({
      field: "fsgEmployeeId",
      value: session?.user?.employeeNumber,
      operation: "equals",
    });
    const fuzzyMatch = q.textFilter({
      field: "fsgEmployeeId",
      value: session?.user?.employeeNumber,
      operation: "contains",
    });
    const filter = q.or(exactMatch, fuzzyMatch);
    filters.push(filter);
  } else {
    const emailMatch = q.textFilter({
      field: "primaryEmail.address",
      value: session?.user?.email,
      operation: "contains",
    });
    filters.push(emailMatch);
  }

  contactQuery.filter(q.and(...filters));

  return contactQuery.getPath();
}

export function AppContextProvider({ children, session }: ProviderProps) {
  // This will run any set up effects needed in the app and scripts related to version upgrades
  useAppSetup();

  // This request looks for a Contact in the database that matches the logged in user, because a user is not currently a Contact entity
  const userContactSearch = useRequestOnMount<APIResponse<ContactResponse>>({
    path: buildUserContactQuery(session),
  });

  const { data: countries, status: countriesStatus } =
    useRequestOnMount<CountryResponseType>(
      { path: ENDPOINTS.GEO.COUNTRIES },
      []
    );

  const divisionsResponse = useRequestOnMount<Division[]>(
    { path: ENDPOINTS.DIVISIONS },
    []
  );

  // use the divisionsFallback array when the response is empty
  const divisions = {
    ...divisionsResponse,
    data:
      Array.isArray(divisionsResponse.data) && divisionsResponse.data.length > 0
        ? divisionsResponse.data
        : divisionsFallback,
  };

  const formRegistry = useRef(new FormRegistry());
  const statesData = useRequestOnMount<StateResponse[]>({
    path: ENDPOINTS.GEO.STATES("US"),
  });
  const provincesData = useRequestOnMount<StateResponse[]>({
    path: ENDPOINTS.GEO.STATES("CA"),
  });
  const servicesData = useRequestOnMount<FSGServices[]>({
    path: ENDPOINTS.SERVICES,
  });
  const { data: sources = [] } = useRequestOnMount<MarketingSource[]>(
    { path: ENDPOINTS.SOURCES },
    []
  );
  const { data: states } = statesData;
  const { data: provinces } = provincesData;
  const { data: services } = servicesData;

  //controlling lead creation from app context
  const [showCreateLead, setShowCreateLead] = useState<boolean>(false);
  const [showCreateSite, setShowCreateSite] = useState<boolean>(false);
  const [onCreateFunctions, setOnCreateFunctions] = useState<CreationSuccess>(
    {}
  );
  const [leadData, setLeadData] = useState<NewLeadFormData | null>(null);
  const [pendingBusinessCardImage, setPendingBusinessCardImage] =
    useState<ImageData | null>(null);
  const [businessCardPhoto, setBusinessCardPhoto] = useState<string | null>(
    null
  );
  const userContact = userContactSearch.data?.data?.filter((contact) =>
    session?.user?.employeeNumber
      ? contact.fsgEmployeeId.toLowerCase() ===
        session?.user?.employeeNumber?.toLowerCase()
      : contact.primaryEmail.address.toLowerCase() ===
          session?.user?.email?.toLowerCase() && contact.fsgEmployeeId !== null
  )[0];

  const isDataReady =
    userContactSearch.status === "success" &&
    countriesStatus === "success" &&
    divisions.status === "success";

  const isCoolKids =
    session?.user?.division === 99 && session?.user?.department === 95;

  const isDiv99 = session?.user?.division === 99;
  const value = {
    countries,
    divisions,
    session,
    userContact,
    showCreateLead: showCreateLead,
    setShowCreateLead: setShowCreateLead,
    showCreateSite: showCreateSite,
    setShowCreateSite: setShowCreateSite,
    leadData: leadData,
    setLeadData: setLeadData,
    businessCardPhoto,
    setBusinessCardPhoto,
    isDataReady,
    isDiv99,
    isCoolKids,
    userContactSearch,
    formRegistry: formRegistry.current,
    onCreateFunctions,
    setOnCreateFunctions,
    states,
    provinces,
    services,
    sources,
    pendingBusinessCardImage,
    setPendingBusinessCardImage,
  };

  return (
    <Provider value={value}>
      <NotificationProvider>{children}</NotificationProvider>
    </Provider>
  );
}

export function useAppContext() {
  const context = React.useContext(AppContext);

  if (context === undefined)
    throw new Error("Cannot call useAppContext outside of AppProvider");

  return context;
}

// This uses a helper function in the generic to infer the return type of the callback function automatically
export function useAppContextSelector<
  T extends (state: AppContextState) => any,
>(callback: T): ReturnType<T> {
  const context = React.useContext(AppContext);

  if (context === undefined)
    throw new Error("Cannot call useAppContextSelector outside of AppProvider");

  return callback(context);
}
